import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { calendarEventTypesQuery } from '../../../api'
import { useGetEventTypesOptions } from '../../../organizationview/integrations/modals/SettingsProcountor/hooks/useGetEventTypesOptions.js'

export const abscencesWithCategories = [
	{
		value: '1P',
		label: '1P Illness (paid)',
		categoryName: 'sicknessAndRehabilitation',
	},
	{
		value: '2P',
		label: '2P Part-time sick leave (paid)',
		categoryName: 'sicknessAndRehabilitation',
	},
	{
		value: '3P',
		label: '3P Parental leave (paid)',
		categoryName: 'familyAndChild',
	},
	{
		value: '4P',
		label: '4P Special pregnancy leave (paid)',
		categoryName: 'familyAndChild',
	},
	{
		value: '5P',
		label: '5P Rehabilitation (paid)',
		categoryName: 'sicknessAndRehabilitation',
	},
	{
		value: '6P',
		label: "6P Child's illness or a compelling family reason (paid)",
		categoryName: 'familyAndChild',
	},
	{
		value: '7P',
		label: '7P Part-time child care leave (paid)',
		categoryName: 'familyAndChild',
	},
	{
		value: '8P',
		label: '8P Training, education (paid)',
		categoryName: 'trainingAndEducation',
	},
	{
		value: '13P',
		label: '13P Leave of absence (paid)',
		categoryName: 'paid',
	},
	{
		value: '14P',
		label: '14P Military refresher training (paid)',
		categoryName: 'paid',
	},
	{ value: '18P', label: 'Mid-week holiday (paid)', categoryName: 'paid' },
	{
		value: '19P',
		label: '19P Accrued holiday (paid)',
		categoryName: 'holidaysAndLeave',
	},
	{
		value: '20P',
		label: '20P Occupational accident (paid)',
		categoryName: 'sicknessAndRehabilitation',
	},
	{
		value: '21P',
		label: '21P Annual leave (paid)',
		categoryName: 'holidaysAndLeave',
	},
	{
		value: '22P',
		label: '22P Part-time absence due to rehabilitation (paid)',
		categoryName: 'sicknessAndRehabilitation',
	},
	{
		value: '23P',
		label: '23P Compensation for annual leave costs (paid)',
		categoryName: 'holidaysAndLeave',
	},
	{
		value: '24P',
		label: '24P Partial special pregnancy leave (paid)',
		categoryName: 'familyAndChild',
	},
	{ value: '99P', label: 'Other (paid)', categoryName: 'paid' },
	{
		value: '1U',
		label: '1U Illness (unpaid)',
		categoryName: 'sicknessAndRehabilitation',
	},
	{
		value: '2U',
		label: '2U Part-time sick leave (unpaid)',
		categoryName: 'sicknessAndRehabilitation',
	},
	{
		value: '3U',
		label: '3U Parental leave (unpaid)',
		categoryName: 'familyAndChild',
	},
	{
		value: '4U',
		label: '4U Special pregnancy leave (unpaid)',
		categoryName: 'familyAndChild',
	},
	{
		value: '5U',
		label: '5U Rehabilitation (unpaid)',
		categoryName: 'sicknessAndRehabilitation',
	},
	{
		value: '6U',
		label: "6U Child's illness or a compelling family reason (unpaid)",
		categoryName: 'familyAndChild',
	},
	{
		value: '7U',
		label: '7U Part-time child care leave (unpaid)',
		categoryName: 'familyAndChild',
	},
	{
		value: '8U',
		label: '8U Training, education (unpaid)',
		categoryName: 'trainingAndEducation',
	},
	{
		value: '9U',
		label: '9U Job alternation leave (unpaid)',
		categoryName: 'unPaid',
	},
	{
		value: '10U',
		label: '10U Study leave (unpaid)',
		categoryName: 'trainingAndEducation',
	},
	{
		value: '11U',
		label: '11U Industrial action or lock-out (unpaid)',
		categoryName: 'unPaid',
	},
	{
		value: '12U',
		label: '12U Interruption in work provision (unpaid)',
		categoryName: 'unPaid',
	},
	{
		value: '13U',
		label: '13U Leave of absence (unpaid)',
		categoryName: 'unPaid',
	},
	{
		value: '14U',
		label: '14U Military refresher training (unpaid)',
		categoryName: 'unPaid',
	},
	{
		value: '15U',
		label: '15U Military or non-military service (unpaid)',
		categoryName: 'unPaid',
	},
	{
		value: '16U',
		label: '16U Lay-off (unpaid)',
		categoryName: 'unPaid',
	},
	{
		value: '17U',
		label: '17U Child care leave (unpaid)',
		categoryName: 'familyAndChild',
	},
	{
		value: '18U',
		label: '18U Mid-week holiday (unpaid)',
		categoryName: 'unPaid',
	},
	{
		value: '99U',
		label: '99U Other (unpaid)',
		categoryName: 'unPaid',
	},
	{ value: 'FLEXI', label: 'Flexi leave', categoryName: 'paid' },
	{ value: 'HOURS', label: 'Hours', categoryName: 'paid' },
]

const sortByLabel = (a, b) => a.label.localeCompare(b.label)

export const absenceNS = 'absencecausecode'
export const translatedCausecode = (code, t) => {
	const options = { ns: absenceNS }
	const numericCode = code.slice(0, -1)
	if (numericCode === '18') return t(code, options) // 18P and 18U have different meanings
	return t(numericCode, options)
}

export const useGetAbscenceTypesOptions = () => {
	const [translationsLoaded, setTranslationsLoaded] = useState(false)
	const [options, setOptions] = useState([])
	const { t, i18n } = useTranslation()

	const eventTypes = useQuery(calendarEventTypesQuery).data || {}
	const eventTypesOptions = useGetEventTypesOptions()
	// refactor when there's time

	useEffect(() => {
		async function fetchNamespaces() {
			await i18n.loadNamespaces([absenceNS]).then(() => {
				setTranslationsLoaded()
				setOptions([
					{
						label: t('WithPay', 'Palkallinen'),
						options: eventTypes.Paid.map((value) => ({
							value,
							label: `${translatedCausecode(value, t)} (${t('WithPay', 'Palkallinen').toLowerCase()})`,
							paidness: eventTypesOptions.find((eventType) => eventType.value === value)?.paidness,
						})).sort(sortByLabel),
					},
					{
						label: t('WithoutPay', 'Palkaton'),
						options: eventTypes.Unpaid.map((value) => ({
							value,
							label: `${translatedCausecode(value, t)} (${t('WithoutPay', 'Palkaton').toLowerCase()})`,
							paidness: eventTypesOptions.find((eventType) => eventType.value === value)?.paidness,
						})).sort(sortByLabel),
					},
					{
						label: t('Others', 'Muut'),
						options: eventTypes.Other.map((value) => ({
							value,
							label: value === 'HOURS' ? t('Hours2', 'Tunnit') : t('FLEXI', 'Saldovapaa'),
							paidness: eventTypesOptions.find((eventType) => eventType.value === value)?.paidness,
						})).sort(sortByLabel),
					},
				])
			})
		}
		!translationsLoaded && eventTypes.Paid && fetchNamespaces()
	}, [i18n.loadNamespaces, eventTypes, t, translationsLoaded, eventTypesOptions.find])

	return { options, abscencesWithCategories }
}

export const getDefaultFormValues = (event, user) => ({
	start: event?.start ? new Date(event.start) : undefined,
	end: event?.end ? new Date(event.end) : undefined,
	info: event?.info ?? '',
	personId: user?.personId,
	personNr: user?.nr,
	personName: user?.name,
	ownAnnouncement: event?.ownAnnouncement ?? false,
	longAbsence: event?.longAbsence ?? false,
	icdCode: event?.icdCode ?? undefined,
	causeCode: undefined, // asetetaan useEffectissä
	approvers: [],
	...event,
})

// approvers is array and mapped id is really a personNumber
const approverFilterMap = (approvers) =>
	approvers.filter((approver) => approver.value).map((approver) => ({ id: Number(approver.value) }))

export const getFormValues = (formValues, approvers, isSickness) => ({
	personId: Number(formValues.personId),
	personNr: formValues.personNr,
	personName: formValues.personName,
	startDate: DateTime.fromJSDate(formValues.start).toISODate(),
	endDate: DateTime.fromJSDate(formValues.end).toISODate(),
	causeCode: formValues.causeCode?.value,
	causeLabel: formValues.causeCode?.label,
	icdCode: isSickness ? formValues.icdCode : undefined,
	status: formValues.status ?? 'OPEN',
	info: formValues.info ?? '',
	ownAnnouncement: formValues.ownAnnouncement,
	longAbsence: formValues.longAbsence ?? false,
	dayHours: formValues.dayHours !== undefined && formValues.dayHours !== '' ? Number(formValues.dayHours) : undefined,
	approvers: approvers?.filter ? approverFilterMap(approvers) : approverFilterMap(approvers.value),
})
